import { Search } from "lucide-react";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ITEM_OPTIONS, LOCATION_OPTIONS } from "@/mocks/mass-update-reference";
import { WORK_ORDER_STATUS_OPTIONS, type WorkOrderSearchFilters } from "@/mocks/search-work-orders";

interface SearchFiltersPanelProps {
  values: WorkOrderSearchFilters;
  onChange: (values: WorkOrderSearchFilters) => void;
  onSearch: () => void;
}

export function SearchFiltersPanel({ values, onChange, onSearch }: SearchFiltersPanelProps) {
  function setField<K extends keyof WorkOrderSearchFilters>(key: K, value: string) {
    onChange({ ...values, [key]: value });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Search</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSearch();
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wo-search-number">Work order #</Label>
              <Input
                id="wo-search-number"
                placeholder="WO-5234"
                value={values.workOrderNo}
                onChange={(event) => setField("workOrderNo", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Item</span>
              <EnumSelect
                value={values.itemCode}
                onValueChange={(value) => setField("itemCode", value)}
                options={ITEM_OPTIONS}
                allOption={{ label: "Any item" }}
                aria-label="Item"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Location</span>
              <EnumSelect
                value={values.locationCode}
                onValueChange={(value) => setField("locationCode", value)}
                options={LOCATION_OPTIONS}
                allOption={{ label: "Any location" }}
                aria-label="Location"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Status</span>
              <EnumSelect
                value={values.status}
                onValueChange={(value) => setField("status", value)}
                options={WORK_ORDER_STATUS_OPTIONS}
                allOption={{ label: "Any status" }}
                aria-label="Status"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wo-start-from">Start date from</Label>
              <Input
                id="wo-start-from"
                type="date"
                value={values.startDateFrom}
                onChange={(event) => setField("startDateFrom", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wo-start-to">Start date to</Label>
              <Input
                id="wo-start-to"
                type="date"
                value={values.startDateTo}
                onChange={(event) => setField("startDateTo", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wo-end-from">End date from</Label>
              <Input
                id="wo-end-from"
                type="date"
                value={values.endDateFrom}
                onChange={(event) => setField("endDateFrom", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wo-end-to">End date to</Label>
              <Input
                id="wo-end-to"
                type="date"
                value={values.endDateTo}
                onChange={(event) => setField("endDateTo", event.target.value)}
              />
            </div>
          </div>
          <div>
            <Button type="submit">
              <Search className="size-4" />
              Search
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
